
import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SERVICES } from '../constants';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Target, 
  Workflow, 
  Zap, 
  ChevronRight, 
  Calendar, 
  ShieldCheck, 
  Globe, 
  FileCheck,
  Plus,
  Cpu,
  HelpCircle,
  PlusCircle,
  MinusCircle
} from 'lucide-react';

const IconMap: Record<string, React.ElementType> = {
  ShieldCheck: ShieldCheck,
  Globe: Globe,
  FileCheck: FileCheck,
};

const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const service = SERVICES.find((s) => s.id === id);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const IconComponent = IconMap[service.icon];
  const otherServices = SERVICES.filter(s => s.id !== id);

  return (
    <div className="pt-32 pb-24 px-6 max-w-7xl mx-auto bg-slate-50 min-h-screen text-slate-900">
      {/* Breadcrumbs / Back */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-12"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-semibold transition-colors group">
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </Link>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Main Content Area */}
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100">
                <IconComponent size={32} />
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-widest">
                Service Overview
              </span>
            </div>
            
            <h1 className="font-display font-extrabold text-4xl md:text-6xl text-slate-900 mb-8 leading-tight tracking-tight">
              {service.title}
            </h1>
            
            <p className="text-slate-600 text-xl leading-relaxed mb-12 max-w-3xl font-medium">
              {service.longDesc}
            </p>

            {/* Benefit Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
              {service.benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl bg-white border border-slate-200 flex items-start gap-4 hover:border-indigo-200 hover:shadow-lg transition-all group shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-all text-indigo-600">
                    <Zap size={20} />
                  </div>
                  <div>
                    <p className="text-slate-900 font-semibold leading-snug">{benefit}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Strategic Focus */}
            <section className="mb-20">
              <div className="p-10 rounded-[2.5rem] bg-indigo-50 border border-indigo-100 relative overflow-hidden group shadow-md">
                <div className="absolute top-0 right-0 p-8 text-indigo-100 group-hover:text-indigo-200 transition-all">
                  <Target size={120} />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 text-indigo-600 font-bold mb-6 uppercase tracking-widest text-sm">
                    <Target size={20} />
                    Core Strategic Focus
                  </div>
                  <blockquote className="text-slate-900 text-2xl md:text-3xl font-display font-bold italic leading-relaxed">
                    "{service.focus}"
                  </blockquote>
                </div>
              </div>
            </section>

            {/* Tech Stack & Tools */}
            {service.tools && (
              <section className="mb-20">
                <h2 className="text-3xl font-display font-bold text-slate-900 mb-10 flex items-center gap-3">
                  <Cpu className="text-indigo-600" size={28} />
                  Tools & Technology Stack
                </h2>
                <div className="flex flex-wrap gap-4">
                  {service.tools.map((tool, i) => (
                    <span key={i} className="px-5 py-3 rounded-xl bg-white border border-slate-200 text-slate-600 font-bold shadow-sm hover:border-indigo-200 transition-colors">
                      {tool}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Comprehensive Scope */}
            <section className="mb-20">
              <h2 className="text-3xl font-display font-bold text-slate-900 mb-10 flex items-center gap-3">
                <CheckCircle2 className="text-indigo-600" size={28} />
                Comprehensive Scope of Work
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {service.scope.map((item, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200 hover:bg-slate-50 transition-all shadow-sm"
                  >
                    <div className="w-5 h-5 mt-1 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 border border-indigo-100">
                      <Plus size={12} />
                    </div>
                    <span className="text-slate-700 font-bold text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Working Methodology */}
            <section className="mb-20">
              <h2 className="text-3xl font-display font-bold text-slate-900 mb-10 flex items-center gap-3">
                <Workflow className="text-indigo-600" size={28} />
                Process & Methodology
              </h2>
              <div className="space-y-0 relative">
                <div className="absolute left-[27px] top-6 bottom-6 w-0.5 bg-slate-200" />
                {service.methodology.map((step, i) => (
                  <motion.div 
                    key={i} 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="relative pl-20 pb-12 last:pb-0 group"
                  >
                    <div className="absolute left-0 top-0 w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-indigo-600 font-bold text-xl group-hover:bg-indigo-600 group-hover:text-white transition-all z-10 shadow-lg shadow-indigo-600/5">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-display font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">{step.title}</h3>
                      <p className="text-slate-600 text-lg leading-relaxed">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Specific Service FAQ */}
            {service.faqs && (
              <section className="mb-20">
                <h2 className="text-3xl font-display font-bold text-slate-900 mb-10 flex items-center gap-3">
                  <HelpCircle className="text-indigo-600" size={28} />
                  Service Specific FAQs
                </h2>
                <div className="space-y-4">
                  {service.faqs.map((faq, i) => (
                    <div key={i} className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
                      <button 
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="text-slate-900 font-bold">{faq.question}</span>
                        {openFaq === i ? <MinusCircle className="text-indigo-600" /> : <PlusCircle className="text-slate-400" />}
                      </button>
                      <AnimatePresence>
                        {openFaq === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-8 pb-6 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-32 space-y-8">
            {/* CTA Card */}
            <div className="p-8 rounded-3xl bg-indigo-600 text-white shadow-2xl shadow-indigo-600/20 relative overflow-hidden group">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
              <h3 className="text-2xl font-display font-bold mb-4 relative z-10">Interested in this Service?</h3>
              <p className="text-indigo-100 mb-8 relative z-10">Let's discuss how we can streamline your {service.title.toLowerCase()} process.</p>
              <a 
                href="/#contact" 
                className="w-full inline-flex items-center justify-center gap-2 py-4 bg-white text-indigo-600 font-bold rounded-xl hover:bg-slate-50 transition-all relative z-10 shadow-lg"
              >
                <Calendar size={18} />
                Schedule Consultation
              </a>
            </div>

            {/* Related Services */}
            <div className="p-8 rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-200/50">
              <h4 className="text-slate-900 font-bold mb-6 text-lg">Other Specialized Services</h4>
              <div className="space-y-4">
                {otherServices.map((other) => {
                  const OtherIcon = IconMap[other.icon];
                  return (
                    <Link 
                      key={other.id} 
                      to={`/service/${other.id}`}
                      className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-all group border border-transparent hover:border-slate-200"
                    >
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-colors border border-indigo-100">
                        <OtherIcon size={20} />
                      </div>
                      <div className="flex-grow">
                        <h5 className="text-slate-700 font-bold text-sm group-hover:text-indigo-600 transition-colors">{other.title}</h5>
                      </div>
                      <ChevronRight size={16} className="text-slate-400 group-hover:text-indigo-600 transition-colors" />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Ethics Badge */}
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white mb-4 border border-slate-200 shadow-sm">
                <ShieldCheck className="text-indigo-600" size={24} />
              </div>
              <h5 className="text-slate-900 font-bold mb-2 text-sm uppercase tracking-wider">Professional Ethos</h5>
              <p className="text-slate-500 text-xs leading-relaxed">
                All services are executed within defined professional boundaries and absolute confidentiality.
              </p>
            </div>
          </div>
        </aside>
      </div>

      {/* Footer Navigation */}
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mt-32 pt-16 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-8"
      >
        <div className="text-center md:text-left">
          <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Ready to move forward?</h3>
          <p className="text-slate-600">I provide structured support tailored to your unique business needs.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/#contact" className="px-8 py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all text-center shadow-lg shadow-indigo-600/20">
            Get in Touch
          </Link>
          <Link to="/blog" className="px-8 py-4 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-center shadow-sm">
            Read Insights
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ServiceDetail;
